import { useOutletContext } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import { BISTRO_INFO, BISTRO_TEL } from "@/lib/bistroInfo";

const MAPS_QUERY = encodeURIComponent(`${BISTRO_INFO.nom}, ${BISTRO_INFO.adresse}`);
const MAPS_EMBED = `https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`;
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`;

export default function Contact() {
  const { horaires } = useOutletContext();

  return (
    <div data-testid="contact-page" className="bg-[#FDFBF7]">
      {/* HERO */}
      <section className="relative h-[44vh] min-h-[300px] overflow-hidden flex items-end">
        <div
          className="absolute inset-0 bg-cover bg-center kenburns"
          style={{ backgroundImage: "url('/images/terrace.jpeg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" aria-hidden />
        <div className="relative z-10 max-w-6xl mx-auto w-full px-6 md:px-10 pb-14 text-white">
          <span className="font-script text-2xl text-[#D4A3A7]">Venez nous voir</span>
          <h1 className="font-serif text-5xl md:text-6xl mt-1">Nous trouver & réserver</h1>
        </div>
      </section>

      {/* MAIN GRID */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-10 items-start">
        {/* RESERVATION COLUMN */}
        <div className="flex flex-col" data-testid="reservation-card">
          <div className="flex items-center gap-3 mb-6">
            <Phone size={22} className="text-[#A61C24]" />
            <h2 className="font-serif text-3xl md:text-4xl">Réservation</h2>
          </div>
          <div className="bg-white border border-[#E5E0D8] p-8 md:p-10 flex flex-col">
            <p className="text-[#1A1A1A]/75 leading-relaxed mb-6">
              Les réservations se font <strong>uniquement par téléphone</strong>. Notre équipe se fera un plaisir de vous accueillir pour un déjeuner d'affaires, en famille ou entre amis.
            </p>

            <a
              href={`tel:${BISTRO_TEL}`}
              data-testid="call-btn"
              className="group flex items-center justify-center gap-4 bg-[#A61C24] hover:bg-[#8B151E] text-white px-6 py-5 transition-colors w-full"
            >
              <Phone size={22} className="shrink-0" />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">Appeler le bistrot</div>
                <div className="font-sans text-2xl font-semibold tabular-nums tracking-wider leading-none mt-1.5">
                  {BISTRO_INFO.telephone}
                </div>
              </div>
            </a>

            <div className="mt-8 pt-6 border-t border-[#E5E0D8] space-y-5">
              <InfoLine icon={<MapPin size={18} />} label="Adresse">
                <p className="leading-relaxed">{BISTRO_INFO.adresse}</p>
              </InfoLine>
              <InfoLine icon={<Mail size={18} />} label="Email">
                <a
                  href={`mailto:${BISTRO_INFO.email}`}
                  className="hover:text-[#A61C24] transition-colors break-all"
                  data-testid="email-link"
                >
                  {BISTRO_INFO.email}
                </a>
              </InfoLine>
            </div>
          </div>
        </div>

        {/* HORAIRES COLUMN */}
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-6">
            <Clock size={22} className="text-[#A61C24]" />
            <h2 className="font-serif text-3xl md:text-4xl">Horaires</h2>
          </div>
          <div className="border border-[#E5E0D8] bg-white divide-y divide-[#E5E0D8]">
            {(horaires || []).map((h) => (
              <div
                key={h.id}
                className="flex justify-between items-start p-5"
                data-testid={`horaire-${h.jour}`}
              >
                <div>
                  <div className="capitalize font-serif text-xl">{h.jour}</div>
                  {h.note && <div className="text-xs text-[#1A1A1A]/60 mt-0.5">{h.note}</div>}
                </div>
                <div className="text-right text-sm">
                  {h.ferme ? (
                    <span className="text-[#A61C24] uppercase tracking-[0.2em]">Fermé</span>
                  ) : (
                    <div className="space-y-0.5">
                      {h.midi_ouverture && (
                        <div>{h.midi_ouverture} – {h.midi_fermeture}</div>
                      )}
                      {h.soir_ouverture && (
                        <div>{h.soir_ouverture} – {h.soir_fermeture}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div
            data-testid="horaires-precision"
            className="mt-5 border-l-2 border-[#A61C24] bg-white px-5 py-4 text-sm leading-relaxed"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#A61C24] mb-2">Bar &amp; Restaurant</p>
            <p className="text-[#1A1A1A]/80">
              <span className="font-serif text-base">Service du restaurant&nbsp;:</span> 12h&ndash;14h le midi, 19h&ndash;22h le soir.
            </p>
            <p className="text-[#1A1A1A]/65 mt-1.5 text-xs">
              Le bar reste ouvert en continu, mais pas de restauration entre 15h et 19h.
            </p>
          </div>
        </div>
      </div>

      {/* MAP SECTION : FULL WIDTH */}
      <section className="border-t border-[#E5E0D8] bg-white" data-testid="map-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="font-script text-2xl text-[#A61C24]">Sur place</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-1">Comment nous rejoindre</h2>
            <p className="text-[#1A1A1A]/70 mt-3 max-w-xl leading-relaxed">
              {BISTRO_INFO.adresse} ; parking aisé à proximité, à quelques minutes du centre de Bourg-en-Bresse.
            </p>
          </div>
          <a
            href={MAPS_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="map-link"
            className="inline-flex items-center gap-2 self-start md:self-auto px-6 py-3 bg-[#A61C24] hover:bg-[#8B151E] text-white text-xs uppercase tracking-[0.25em] transition-colors"
          >
            Itinéraire
            <ExternalLink size={14} />
          </a>
        </div>
        <div className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
          <div className="aspect-[16/7] border border-[#E5E0D8] overflow-hidden shadow-sm">
            <iframe
              title="Plan d'accès – Le Bistrot de la Neuve"
              src={MAPS_EMBED}
              className="w-full h-full grayscale-[15%]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoLine({ icon, label, children }) {
  return (
    <div className="flex gap-4">
      <div className="text-[#A61C24] mt-1 shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#1A1A1A]/55 mb-1">{label}</div>
        <div className="text-[#1A1A1A]/85 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
